import { useContext } from 'react';

import {
  faArrowRight,
  faPrint,
  faQrcode,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/videa-components/src/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import classNames from 'classnames';
import { useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { IframeMessageType, ModalType } from '@/app/external/types';
import { usePreferences } from '@/react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { useGenerateQRCode } from '../casePresentation/patientDownloadable/hooks/useGenerateQRCode';
import { usePatientPrintoutData } from '../casePresentation/patientDownloadable/hooks/usePatientPrintoutData';
import {
  isPreviewOpenAtom,
  previewDisplayDeviceAtom,
  previewModeAtom,
  showSegmentationAtom,
} from '../multiFileViewer/imageToolbar/atoms';

interface ShareWithPatientDropdownProps {
  hasActiveImage: boolean;
  source: string;
  qrCodeEnabled?: boolean;
}

export const ShareWithPatientDropdown = ({
  hasActiveImage,
  source,
  qrCodeEnabled = true,
}: ShareWithPatientDropdownProps) => {
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    imageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const setIsPreviewOpen = useSetAtom(isPreviewOpenAtom);
  const setPreviewMode = useSetAtom(previewModeAtom);
  const setPreviewDisplayDevice = useSetAtom(previewDisplayDeviceAtom);
  const { trackEvent } = useTrackEvent();
  const setShowSegmentation = useSetAtom(showSegmentationAtom);
  const { data: preferences } = usePreferences();
  const { id: selectedPatientId } = useParams<{ id: string }>();

  const { visitSummaryResponse, mixPanelData } = usePatientPrintoutData();
  const { generateQRCode } = useGenerateQRCode();

  // Check if there are visit summaries available
  const hasVisitSummaries = (visitSummaryResponse?.data?.length ?? 0) > 0;

  const handleQRCodeClick = async () => {
    trackEvent(mixpanelEvents.visitSidebarQRCode, {
      source,
      ...mixPanelData,
      isIframe: !!isIframe,
      externalPatientId,
      externalImageId,
      patientId: selectedPatientId ?? patientId,
      trialStatus,
      centralizedBilling: isCentralizedBilling,
    });
    if (isIframe) {
      window.parent.postMessage(
        {
          type: IframeMessageType.MODAL_OPEN,
          payload: {
            type: ModalType.QR_CODE,
            patientId,
            imageId,
            externalPatientId,
            externalImageId,
          },
        },
        '*',
      );
      return;
    }

    setShowSegmentation(true);
    setIsPreviewOpen(true);
    setPreviewMode('qrCode');
    setPreviewDisplayDevice('mobile');

    if (visitSummaryResponse?.data?.length) {
      await generateQRCode();
    }
  };

  const handlePrintDownloadClick = () => {
    trackEvent(mixpanelEvents.visitSidebarPrintDownload, {
      source,
      ...mixPanelData,
      isIframe: !!isIframe,
      externalPatientId,
      externalImageId,
      patientId: selectedPatientId ?? patientId,
      trialStatus,
      centralizedBilling: isCentralizedBilling,
    });

    if (isIframe) {
      window.parent.postMessage(
        {
          type: IframeMessageType.MODAL_OPEN,
          payload: {
            type: ModalType.PRINT_DOWNLOAD,
            patientId,
            imageId,
            externalPatientId,
            externalImageId,
          },
        },
        '*',
      );
      return;
    }

    setShowSegmentation(true);
    setIsPreviewOpen(true);
    setPreviewMode('print');
    setPreviewDisplayDevice('desktop');
  };

  return (
    <Tooltip delayDuration={0} eager>
      <TooltipTrigger asChild>
        <div
          className={classNames({
            'vai-cursor-not-allowed': !hasActiveImage,
            'share-with-patient': hasActiveImage,
          })}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                  data-testid="share-with-patient-button"
                  variant="default"
                  size="default"
                  className={classNames(
                    'vai-w-full vai-h-full !vai-px-2 vai-py-3 [@media(max-height:570px)]:!vai-py-2 !vai-gap-1',
                    'vai-flex vai-justify-center vai-items-center vai-gap-2',
                    '!vai-whitespace-normal',
                    {
                      'vai-bg-sidebar-border hover:vai-bg-sidebar-background':
                        hasActiveImage,
                      '!vai-bg-grey-600 vai-pointer-events-none !vai-text-grey-500':
                        !hasActiveImage,
                    },
                  )}
                >
                  <span className="vai-text-sm [@media(max-height:570px)]:vai-text-[12px] vai-font-medium vai-text-center vai-break-words vai-leading-tight">
                    <Trans>Share with Patient</Trans>
                  </span>
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="vai-h-3.5 vai-w-3.5 vai-transform vai-rotate-[-45deg]"
                  />
                </Button>
              </DropdownMenuTrigger>
              {hasActiveImage && (
                <DropdownMenuContent
                  align="end"
                  side="top"
                  className={classNames(
                    'vai-w-48',
                    'vai-bg-sidebar-backgroundDark vai-border vai-border-sidebar-border',
                    'vai-rounded-lg vai-shadow-lg vai-py-2',
                  )}
                >
                  <DropdownMenuItem
                    data-testid="print-download-dropdown-item"
                    className={classNames(
                      'vai-gap-3',
                      'vai-text-white',
                      'hover:vai-cursor-pointer',
                    )}
                    onClick={handlePrintDownloadClick}
                  >
                    <FontAwesomeIcon icon={faPrint} />
                    <span>
                      <Trans>Print/Download</Trans>
                    </span>
                  </DropdownMenuItem>
                  {preferences?.showQRCodeButton && (
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger
                        asChild
                        data-testid="qr-code-dropdown-item"
                      >
                        <DropdownMenuItem
                          disabled={!qrCodeEnabled || !hasVisitSummaries}
                          className={classNames(
                            'vai-gap-3',
                            'vai-text-white',
                            {
                              'hover:vai-cursor-pointer':
                                qrCodeEnabled && hasVisitSummaries,
                              'vai-cursor-not-allowed vai-opacity-50':
                                !qrCodeEnabled || !hasVisitSummaries,
                            },
                          )}
                          onClick={
                            qrCodeEnabled && hasVisitSummaries
                              ? handleQRCodeClick
                              : undefined
                          }
                        >
                          <FontAwesomeIcon icon={faQrcode} />
                          <span>
                            <Trans>QR Code</Trans>
                          </span>
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      {(!qrCodeEnabled || !hasVisitSummaries) && (
                        <TooltipContent>
                          <p>
                            {!hasVisitSummaries ? (
                              <Trans>
                                QR code not available for this patient
                              </Trans>
                            ) : (
                              <Trans>Coming soon</Trans>
                            )}
                          </p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  )}
                </DropdownMenuContent>
              )}
          </DropdownMenu>
        </div>
      </TooltipTrigger>
      {!hasActiveImage && (
        <TooltipContent>
          <p>
            <Trans>Please select an image to share with patient</Trans>
          </p>
        </TooltipContent>
      )}
    </Tooltip>
  );
};
